import { sva } from "@stubhub/broadway-foundation/css";

export const featuredShowEventsModule = sva({
  className: "featuredShowEventsModule",
  slots: [
    "root",
    "header",
    "profile",
    "headerText",
    "kicker",
    "title",
    "headerArrow",
    "carousel",
    "row",
    "prevArrow",
    "nextArrow",
    "card",
    "cardTop",
    "cardText",
    "date",
    "location",
    "price",
  ],
  base: {
    root: {
      display: "flex",
      flexDirection: "column",
      rowGap: "primitives.200",
      width: "full",
    },
    header: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      columnGap: "primitives.150",
    },
    profile: {
      width: "primitives.700",
      height: "primitives.700",
      flexShrink: "[0]",
      objectFit: "cover",
      borderStartStartRadius: "primitives.150",
      borderStartEndRadius: "primitives.150",
      borderEndEndRadius: "primitives.150",
      borderEndStartRadius: "primitives.150",
    },
    headerText: {
      display: "flex",
      flexDirection: "column",
      rowGap: "primitives.25",
      alignItems: "flex-start",
    },
    // Figma: label/small.
    kicker: {
      textStyle: "label.small",
      color: "[#091218]",
      fontWeight: "medium",
    },
    // Figma: Heading header/medium — the show name under the "15 outrageous years" kicker.
    // 20/24 on phone, 24/28 from tablet up. (25d0473ef4 had narrowed this to label/medium, 16/20,
    // which read too small.)
    title: {
      textStyle: "header.medium",
      color: "[#091218]",
      fontWeight: "bold",
    },
    carousel: {
      position: "relative",
      width: "full",
    },
    row: {
      display: "flex",
      flexDirection: "row",
      // Phone: 172px cards on an 8px gutter.
      columnGap: "primitives.100",
      alignItems: "flex-start",
      overflowX: "auto",
      overflowY: "hidden",
      scrollSnapType: "[x mandatory]",
      // Phone: the row bleeds to the right screen edge only, so a further card peeks off the right
      // (the scroll affordance) while the left stays clipped at the page gutter and the first card
      // remains aligned with the header text. The page gutter is 16px on phone (Container's phone
      // padding = spacing(4)), so the right negative margin cancels it and the trailing inline
      // padding keeps the last card's inset matching the left.
      //
      // Width must stay auto: a negative inline-end margin only widens an auto-width block. An
      // explicit `width: full` pins the box 16px short of the viewport, which is why the bleed did
      // not reach the edge — the shared carousel (Top Performers) relies on the same auto behaviour.
      marginInlineStart: "primitives.0",
      marginInlineEnd: "[-16px]",
      paddingInlineStart: "primitives.0",
      paddingInlineEnd: "primitives.200",
      tablet: {
        columnGap: "primitives.200",
        marginInlineStart: "primitives.0",
        marginInlineEnd: "primitives.0",
        paddingInlineStart: "primitives.0",
        paddingInlineEnd: "primitives.0",
      },
      // Hide the scrollbar, as the shared carousel does — the arrows are the affordance.
      scrollbarWidth: "[none]",
      "&::-webkit-scrollbar": { display: "none" },
      "& > *": { scrollSnapAlign: "[start]" },
    },
    // Opaque surface so the arrow stays readable over the cards, matching the shared carousel.
    // Mobile moves the affordance up into the header (see `headerArrow`), so the edge arrows are
    // tablet-and-up only.
    prevArrow: {
      display: "none",
      tablet: {
        display: "flex",
        position: "absolute",
        insetBlockStart: "[50%]",
        insetInlineStart: "primitives.0",
        translate: "[-50% -50%]",
        zIndex: "[1]",
        backgroundColor: "bg.surface",
      },
    },
    // Desktop straddles the carousel's right edge, vertically centred like Top Performers' arrow.
    nextArrow: {
      display: "none",
      tablet: {
        display: "flex",
        position: "absolute",
        insetBlockStart: "[50%]",
        insetInlineEnd: "primitives.0",
        translate: "[50% -50%]",
        zIndex: "[1]",
        backgroundColor: "bg.surface",
      },
    },
    // Phone only: sits in the header row, right of the kicker/title, above the events.
    headerArrow: {
      marginInlineStart: "auto",
      flexShrink: "[0]",
      backgroundColor: "bg.surface",
      tablet: {
        display: "none",
      },
    },
    card: {
      display: "flex",
      flexDirection: "column",
      rowGap: "primitives.200",
      justifyContent: "center",
      alignItems: "flex-start",
      // Phone: 172px, tablet and up: 218px.
      width: "[172px]",
      tablet: {
        width: "[218px]",
      },
      flexShrink: "[0]",
      paddingBlockStart: "primitives.150",
      paddingBlockEnd: "primitives.100",
      paddingInlineStart: "primitives.100",
      paddingInlineEnd: "primitives.100",
      boxSizing: "border-box",
      backgroundColor: "[#FFFFFF]",
      borderBlockWidth: "primitives.25",
      borderInlineWidth: "primitives.25",
      borderBlockStyle: "solid",
      borderInlineStyle: "solid",
      borderBlockColor: "[#dfe3e5]",
      borderInlineColor: "[#dfe3e5]",
      borderStartStartRadius: "primitives.200",
      borderStartEndRadius: "primitives.200",
      borderEndEndRadius: "primitives.200",
      borderEndStartRadius: "primitives.200",
      textDecorationLine: "none",
    },
    cardTop: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      columnGap: "primitives.150",
      width: "full",
      minHeight: "primitives.500",
    },
    cardText: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      flexGrow: "[1]",
      flexBasis: "[0]",
      minWidth: "primitives.0",
    },
    // Figma: label/small, bold — the card's leading line.
    date: {
      textStyle: "label.small",
      color: "[#091218]",
      fontWeight: "bold",
    },
    // Figma: label/small.
    location: {
      textStyle: "label.small",
      color: "[#091218]",
      width: "full",
      whiteSpace: "nowrap",
      overflowX: "hidden",
      textOverflow: "ellipsis",
    },
    price: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      alignSelf: "stretch",
      minHeight: "primitives.400",
      paddingBlockStart: "primitives.75",
      paddingBlockEnd: "primitives.75",
      paddingInlineStart: "primitives.150",
      paddingInlineEnd: "primitives.150",
      backgroundColor: "bg.surface.stronger",
      color: "[#091218]",
      textStyle: "label.small",
      fontWeight: "medium",
      textAlign: "center",
      borderStartStartRadius: "primitives.100",
      borderStartEndRadius: "primitives.100",
      borderEndEndRadius: "primitives.100",
      borderEndStartRadius: "primitives.100",
    },
  },
});
